import axios, { AxiosError } from 'axios';
import { notification } from 'antd';
import get from 'lodash/get';
import errors from 'App/lib/errors';

const http = axios.create({
  withCredentials: true,
  headers: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'X-Requested-With': 'XMLHttpRequest',
  },
});

/**
 * Allows you to register error handlers for specific status codes (if any)
 */
export const addErrorInterceptor = (
  statuses: (number | undefined)[],
  interceptor: (error: AxiosError, status: number | null) => unknown
): void => {
  http.interceptors.response.use(undefined, (error: AxiosError) => {
    const status = get(error, 'response.status');

    if (statuses.length > 0 && !statuses.includes(status)) {
      return Promise.reject(error);
    }
    return interceptor(error, status);
  });
};

/**
 * Notifies users something went wrong.
 * undefined error is network disconnected - mobile device offline?
 * @param error
 */
addErrorInterceptor([500, 504, 400, 403, 405, undefined], (error, status) => {
  const {
    message,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    description = (error.response?.data as any)?.message ??
      (null as string | null),
    icon,
    type,
  } = get(errors, status || 0, errors.default);

  // prevent duplicate notifications
  const key = navigator.onLine ? description : 'You appear to be offline';
  notification.close(key);

  notification[type]({
    key,
    message,
    description: key,
    icon,
    duration: 7,
  });
  return Promise.reject(error);
});

/**
 * Whenever a 419 response is returned, we need to refresh the CSRF cookie then resend the request.
 * @param error
 */
addErrorInterceptor([419], (error: AxiosError) => {
  const requestConfig = error.config;
  if (requestConfig === undefined) {
    return Promise.reject(error);
  }
  return http
    .get('/sanctum/csrf-cookie')
    .then(() => http.request(requestConfig));
});

export default http;
